import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

/**
 * Owns the message stream for the customer ChatPage:
 *   • Initial fetch + every-2s polling of `/chats/:id/messages?after=`
 *   • Every-3s polling of `/chats/:id/read-status` so the customer's own
 *     bubbles flip grey-tick → blue-tick in near real-time.
 *   • Local `messages` and `isTyping` state.
 *   • Smart typing-indicator: if a non-customer message lands but does NOT
 *     end with a "?" (likely an AI chunk with more to come), flash the
 *     typing dots again for ~4s.
 *   • Auto-scrolls `scrollRef.current` to the bottom whenever the visible
 *     content changes.
 *   • Exposes helpers so the parent's "send", "pick my own chart" and
 *     "submit external chart" flows can splice into the same source of
 *     truth without re-fetching.
 *
 * @param chatId    the active chat's id
 * @param chat      the chat doc (used only to gate polling on mount)
 * @param scrollRef ref to the messages-list scrollable container
 * @param options
 *        - onIncomingMessage(): fired once whenever new non-customer
 *          messages arrive — caller uses this to mark the chat read in
 *          the global OngoingChatContext.
 * @returns {
 *   messages, setMessages,
 *   isTyping, setIsTyping,
 *   readStatus,
 *   appendSentMessage(msg),       // splice locally after POST /messages
 *   replaceAllMessages(msgs),     // wholesale reset (e.g. ext-chart submit)
 *   expectReply(),                // optimistically flash typing dots
 * }
 */
export function useChatMessages({ chatId, chat, scrollRef, onIncomingMessage, onChatStatusChange } = {}) {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [readStatus, setReadStatus] = useState({});

  const lastTs = useRef(null);
  const pollRef = useRef(null);
  const readPollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const [peerTyping, setPeerTyping] = useState(false);
  const lastTypingPingRef = useRef(0);

  // Debounced "I am typing" heartbeat. Caller invokes from the input's
  // onChange — we throttle to once per 2.5s so the backend doesn't get
  // hammered with one POST per keystroke.
  const pingTyping = () => {
    const now = Date.now();
    if (now - lastTypingPingRef.current < 2500) return;
    lastTypingPingRef.current = now;
    api.post(`/chats/${chatId}/typing`).catch(() => { /* best-effort */ });
  };

  // Incremental message poll — ADAPTIVE cadence so a quiet chat (both
  // sides idle for >10s) doesn't keep hammering the backend. Active
  // conversations stay snappy (2s), idle chats relax to 4 → 6 → 8s. Any
  // new message from either side resets the interval to 2s immediately.
  // This was the single biggest source of preview→prod latency when
  // multiple human astrologers were live concurrently.
  useEffect(() => {
    if (!chat || !chatId) return;
    let cancelled = false;
    let timeoutId = null;
    let emptyStreak = 0;
    const fetchMessages = async () => {
      try {
        const { data } = await api.get(`/chats/${chatId}/messages`, {
          params: lastTs.current ? { after: lastTs.current } : {},
        });
        if (cancelled) return;
        if (data.length) {
          emptyStreak = 0; // ACTIVITY — reset to fast cadence
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.message_id));
            return [...prev, ...data.filter((m) => !seen.has(m.message_id))];
          });
          lastTs.current = data[data.length - 1].created_at;
          onIncomingMessage?.();

          // Typing-indicator chunking heuristic
          const incomingNonCust = data.filter((m) => m.sender_role !== "customer");
          if (incomingNonCust.length) {
            setIsTyping(false);
            if (typingTimerRef.current) {
              clearTimeout(typingTimerRef.current);
              typingTimerRef.current = null;
            }
            const latest = incomingNonCust[incomingNonCust.length - 1];
            const txt = (latest.text || "").trim();
            const isFinalChunk = txt.endsWith("?") || latest.kind === "kundali_chart";
            if (!isFinalChunk) {
              setTimeout(() => { if (!cancelled) setIsTyping(true); }, 250);
              typingTimerRef.current = setTimeout(
                () => { if (!cancelled) setIsTyping(false); }, 4000,
              );
            }
          }
        } else {
          emptyStreak += 1;
        }
      } catch (err) { console.error("Failed to fetch messages", err); }
      if (cancelled) return;
      // 2s active → 4s after 5 quiet polls (= ~10s of silence) → 6s
      // after 10 (~20s) → 8s ceiling. `>=` so cadence shifts at exactly
      // the PRD-cited 10s and 20s marks (was off by ~2s previously).
      let nextDelay = 2000;
      if (emptyStreak >= 10) nextDelay = 8000;
      else if (emptyStreak >= 5) nextDelay = 4000;
      timeoutId = setTimeout(fetchMessages, nextDelay);
    };
    fetchMessages();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.chat_id]);

  // Read-receipts + peer-typing + chat-status poll. Combines 2 GETs each
  // tick so the backend doesn't see 2 separate requests per second. Once
  // the chat is ENDED, we slow this poll to 30s (just enough to catch
  // post-end follow-up reads); when ACTIVE it stays at 2s for snappy
  // double-tick + typing-indicator updates.
  useEffect(() => {
    if (!chat || !chatId) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const [rs, ts] = await Promise.all([
          api.get(`/chats/${chatId}/read-status`),
          api.get(`/chats/${chatId}/typing-status`),
        ]);
        if (cancelled) return;
        setReadStatus(rs.data || {});
        setPeerTyping(!!ts.data?.astrologer_typing);
        // Status changed from active → ended? Surface immediately so the
        // "Continue" / "Consultation paused" bar appears on the spot.
        const newStatus = ts.data?.chat_status;
        if (newStatus && newStatus !== chat.status) {
          // Pull the full chat + billing snapshot so all derived UI
          // (rating prompt, resume bar, message-input disable) updates
          // in a single render tick.
          try {
            const [{ data: refreshed }, { data: b }] = await Promise.all([
              api.get(`/chats/${chatId}`),
              api.get(`/chats/${chatId}/billing`),
            ]);
            if (!cancelled) {
              onChatStatusChange?.(refreshed, b);
            }
          } catch { /* ignore */ }
        }
      } catch (_) { /* ignore */ }
    };
    pull();
    const cadence = chat?.status === "ended" ? 30000 : 2000;
    readPollRef.current = setInterval(pull, cadence);
    return () => { cancelled = true; clearInterval(readPollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.chat_id, chat?.status]);

  // Auto-scroll
  useEffect(() => {
    scrollRef?.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isTyping, scrollRef]);

  const appendSentMessage = (msg) => {
    setMessages((prev) => [...prev, msg]);
    lastTs.current = msg.created_at;
  };

  const replaceAllMessages = (msgs) => {
    setMessages(msgs);
    lastTs.current = msgs.length ? msgs[msgs.length - 1].created_at : lastTs.current;
  };

  const expectReply = (timeoutMs = 60000) => {
    setIsTyping(true);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => setIsTyping(false), timeoutMs);
  };

  return {
    messages,
    setMessages,
    isTyping,
    setIsTyping,
    peerTyping,
    pingTyping,
    readStatus,
    appendSentMessage,
    replaceAllMessages,
    expectReply,
  };
}
